import type { CustomField, PatternBased, RuleBased, RuleSet } from '@kkp-fraud/shared';
import type { RuleKind } from './hooks/useRuleSetDraft';
import { effectiveFeederConfig } from './feederFields';

/**
 * Publishing ONE rule out of a draft that has several edits in it.
 *
 * A release is still the whole document — the engine only takes whole documents — so the
 * row's "Publish" posts the ACTIVE document with just that one rule swapped for its draft
 * copy. Once that is live, the draft is rebuilt on top of the new active document and every
 * other edit the operator had made is put back, so publishing one rule never costs them the
 * rest of their work.
 */

type AnyRule = RuleBased | PatternBased;

/** Set-level fields a draft can change. `version` and `updatedUtc` belong to the release. */
const META_KEYS = ['thresholds', 'failSafe', 'note', 'defaultAction', 'feederFields', 'alerts', 'customFields'] as const;
const KINDS: readonly RuleKind[] = ['ruleBased', 'patternBased'];

/** JSON with object keys sorted, so two copies of a rule compare equal whatever their key order. */
function canonical(v: unknown): string {
  return JSON.stringify(v ?? null, (_k, value: unknown) =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(Object.entries(value as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)))
      : value,
  );
}

export function sameRule(a: AnyRule | undefined, b: AnyRule | undefined): boolean {
  return canonical(a) === canonical(b);
}

function listOf(rs: RuleSet, kind: RuleKind): AnyRule[] {
  return ((rs[kind] ?? []) as AnyRule[]).slice();
}

export function findRule(rs: RuleSet | null | undefined, kind: RuleKind, id: string): AnyRule | undefined {
  return rs ? listOf(rs, kind).find((r) => r.id === id) : undefined;
}

/** Replace (or append) one rule, keeping its position when it already exists. */
function withRule(rs: RuleSet, kind: RuleKind, rule: AnyRule): RuleSet {
  const list = listOf(rs, kind);
  const at = list.findIndex((r) => r.id === rule.id);
  if (at >= 0) list[at] = rule;
  else list.push(rule);
  return { ...rs, [kind]: list } as RuleSet;
}

function withoutRule(rs: RuleSet, kind: RuleKind, id: string): RuleSet {
  return { ...rs, [kind]: listOf(rs, kind).filter((r) => r.id !== id) } as RuleSet;
}

/** True when the draft's copy of this rule differs from the active one (or only one side has it). */
export function ruleChanged(loaded: RuleSet | null, draft: RuleSet | null, kind: RuleKind, id: string): boolean {
  if (!loaded || !draft) return false;
  return !sameRule(findRule(loaded, kind, id), findRule(draft, kind, id));
}

/**
 * The document a row's Publish releases: `loaded` exactly, except that this one rule is the
 * draft's copy — added when it is new in the draft, removed when the draft deleted it.
 * Version, thresholds, fail-safe, default and every other rule are `loaded`'s.
 */
export function candidateWithOnly(loaded: RuleSet, draft: RuleSet, kind: RuleKind, id: string): RuleSet {
  const drafted = findRule(draft, kind, id);
  return drafted ? withBindingsFor(withRule(loaded, kind, drafted), draft, drafted) : withoutRule(loaded, kind, id);
}

/** Every field name a rule reads: its clauses and rows, `requires`, and a pattern rule's filters. */
function namesReadBy(rule: AnyRule): Set<string> {
  const names = new Set<string>();
  const clauses = (list: readonly { field: string }[] | undefined) => list?.forEach((c) => names.add(String(c.field)));
  if ('clauses' in rule) {
    clauses(rule.clauses);
    rule.branches?.forEach((b) => clauses(b.clauses));
    rule.requires?.forEach((f) => names.add(String(f)));
  } else {
    clauses(rule.filters);
    rule.branches?.forEach((b) => b.measures?.forEach((m) => clauses(m.filters)));
  }
  return names;
}

/**
 * Wave 6 — a rule reading a custom field the release does not declare yet (or declares
 * differently) is published WITH the draft's declaration of it: the engine refuses a clause on a
 * name it cannot resolve. Only the bindings this rule reads travel; every other binding stays as
 * released, and `reapplyOtherEdits` puts the rest of the draft's back afterwards.
 */
function withBindingsFor(candidate: RuleSet, draft: RuleSet, rule: AnyRule): RuleSet {
  const read = namesReadBy(rule);
  const needed = (draft.customFields ?? []).filter((f) => read.has(f.name));
  const released = candidate.customFields ?? [];
  if (needed.every((f) => released.some((r) => canonical(r) === canonical(f)))) return candidate;
  const merged: CustomField[] = released.filter((r) => !needed.some((f) => f.name === r.name));
  merged.push(...needed);
  const next: RuleSet = { ...candidate, customFields: merged };
  // A binding to a feeder row the draft added needs that row too (`custom_field_from_unknown`).
  const keys = new Set(effectiveFeederConfig(candidate).map((r) => r.key));
  if (draft.feederFields && needed.some((f) => !keys.has(f.from))) next.feederFields = draft.feederFields;
  return next;
}

/**
 * The draft after a scoped release went live: `published`, with every OTHER change the old
 * draft carried (relative to the document it was taken from) applied again — rules edited,
 * added or deleted, and set-level settings. The rules in `except` are left as published.
 */
export function reapplyOtherEdits(
  published: RuleSet,
  oldLoaded: RuleSet,
  oldDraft: RuleSet,
  except: string | readonly string[],
): RuleSet {
  const skip = new Set(typeof except === 'string' ? [except] : except);
  let next: RuleSet = { ...published };

  for (const kind of KINDS) {
    const before = listOf(oldLoaded, kind);
    const after = listOf(oldDraft, kind);
    for (const rule of after) {
      if (skip.has(rule.id)) continue;
      const was = before.find((r) => r.id === rule.id);
      if (!sameRule(was, rule)) next = withRule(next, kind, rule);
    }
    for (const rule of before) {
      if (skip.has(rule.id)) continue;
      if (!after.some((r) => r.id === rule.id)) next = withoutRule(next, kind, rule.id);
    }
  }

  for (const key of META_KEYS) {
    if (canonical(oldLoaded[key]) === canonical(oldDraft[key])) continue;
    if (oldDraft[key] === undefined) {
      const { [key]: _gone, ...rest } = next;
      next = rest as RuleSet;
    } else {
      next = { ...next, [key]: oldDraft[key] } as RuleSet;
    }
  }

  return next;
}
